import { RefObject, useCallback, useEffect, useRef } from 'react';

export const INFINITE_SCROLL_ROOT_MARGIN = '0px 0px 400px 0px';

type UseInfiniteScrollSentinelParams = {
  /**
   * The element the caller believes scrolls. Used only when it really does: a wrapper can carry
   * `overflow-y: auto` and still be laid out at its full content height, with the scrolling
   * happening further up.
   */
  rootRef?: RefObject<HTMLElement | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Number of items rendered, so the observer re-subscribes once the list has grown. */
  itemCount: number;
};

function scrolls(element: HTMLElement) {
  const overflowY = getComputedStyle(element).overflowY;
  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight + 1;
}

/**
 * RISK: `rootMargin` is measured against the root's own box, so observing against an element that
 * does not scroll keeps the sentinel permanently in view and pulls every batch at once. Falls back
 * to the nearest ancestor that actually scrolls, and to the viewport when none does yet — the
 * effect re-subscribes as the list grows, so a list that starts too short to scroll corrects
 * itself once it does.
 */
function resolveScrollRoot(
  sentinel: HTMLElement,
  preferred: HTMLElement | null
): HTMLElement | null {
  if (preferred && scrolls(preferred)) return preferred;

  let ancestor = sentinel.parentElement;
  while (ancestor && ancestor !== document.body) {
    if (scrolls(ancestor)) return ancestor;
    ancestor = ancestor.parentElement;
  }

  return null;
}

export function useInfiniteScrollSentinel({
  rootRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  itemCount,
}: UseInfiniteScrollSentinelParams) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: resolveScrollRoot(sentinel, rootRef?.current ?? null),
      rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
      threshold: 0.1,
    });

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [handleIntersection, rootRef, itemCount]);

  return sentinelRef;
}
